import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    getPreloadedElectionDefinition,
    getSeatsData,
    loadElectionDefinition,
    searchSeatsOrCandidates,
    SeatData,
    sortMethods,
    sortSeats,
    transformAreaData,
} from '../data'
import { ElectionFilter, ElectionText } from '../components'
import { FullElectorateCard } from '../FullElectorateCard/FullElectorateCard'
import {
    useQueryParams,
    AllEvents,
    DataLayerEventName,
    ElectionDefinition,
    ConfigurationContext,
    LoadedElectionDefinition,
} from '@news-mono/web-common'
import { CustomPageSharing } from '../../../buttons/SharingCustomPage/CustomPageSharing'
import { ElectionSearchInput } from '../components/ElectionSearch'
import {
    StyledElectorateTitle,
    StyledElectorateWidgetContainer,
    StyledTitleContainer,
} from './FullElectorateWidget.styled'
import {
    TheSeatsDivider,
    TheSeatsNoResultsText,
} from '../TheSeatsWidget/TheSeatsWidget.styled'

export interface FullElectorateWidgetProps {
    electionDefinition: ElectionDefinition
    onEvent: (event: AllEvents) => void
    imageBaseUrl?: string
}

/** @TODO:
 * Currently searching is not working, this is due to the query params. Works without specifying a param.
 */
export const FullElectorateWidget = ({
    electionDefinition,
    onEvent,
    imageBaseUrl,
}: FullElectorateWidgetProps): JSX.Element => {
    const [searchTerm, setSearchTerm] = useState<string>('')
    const { queryParams, pathname } = useQueryParams()
    const config = useContext(ConfigurationContext)

    const filterResults = useCallback(
        (searchTerm: string, seatData: SeatData[]) => {
            setSearchTerm(searchTerm)
            return searchSeatsOrCandidates(seatData, searchTerm)
        },
        [],
    )
    const getSeatDataFromDefinition = useCallback(
        (loadedDefinition: LoadedElectionDefinition) => {
            // Check for search query param on load
            const searchParams = queryParams.get('search')

            if (loadedDefinition.electionData.data) {
                const transformedData = transformAreaData(
                    loadedDefinition.electionData.data,
                )
                const seatData = getSeatsData(
                    transformedData,
                    imageBaseUrl,
                    144,
                )

                const filteredData = searchParams
                    ? filterResults(searchParams, seatData)
                    : seatData

                return filteredData
            } else {
                console.warn(
                    'No data was found in the loaded election definition for FullElectorateWidget!',
                )
            }
        },
        [filterResults, imageBaseUrl, queryParams],
    )

    // If the provided election data is already loaded, we can use that without relying on a fetch
    const loadedDefinition = getPreloadedElectionDefinition(electionDefinition)
    const [storedDefinition, setStoredDefinition] = useState<
        LoadedElectionDefinition | undefined
    >(loadedDefinition || undefined)
    let initialSeatData: SeatData[] | undefined
    if (loadedDefinition) {
        initialSeatData = getSeatDataFromDefinition(loadedDefinition)
    }

    // This stores the filtered seat data, that way when the search input is cleared, we can revert back to the original data
    const [seatData, setSeatData] = useState<SeatData[] | undefined>(
        initialSeatData,
    )

    // Reset initial data if it is empty (this can happpen during client side nav) also check for search query params
    useEffect(() => {
        const renderDataFromDefinition = (
            definition: LoadedElectionDefinition,
        ) => {
            // Seat data is found correctly from the loaded definition, let's update it!
            const renderedSeatData = getSeatDataFromDefinition(definition)
            if (
                renderedSeatData &&
                (!seatData || renderedSeatData.length !== seatData.length)
            ) {
                setSeatData(renderedSeatData)
            }

            if (!renderedSeatData) {
                console.warn(
                    'No data was found in the loaded election definition for FullElectorateWidget!',
                )
            }
        }
        const load = async () => {
            const loadedDefinition = await loadElectionDefinition(
                electionDefinition,
                config,
            )

            renderDataFromDefinition(loadedDefinition)
            setStoredDefinition(loadedDefinition)
        }

        if (storedDefinition) {
            renderDataFromDefinition(storedDefinition)
        } else {
            load()
        }
    }, [
        seatData,
        queryParams,
        filterResults,
        electionDefinition,
        config,
        getSeatDataFromDefinition,
        storedDefinition,
    ])

    // We need to track which sort method the user has selected, so we can ensure the served data always follows this.
    const [sortBy, setSortBy] = useState(sortMethods[0])
    const sortedData = useMemo(
        () => sortSeats(seatData, sortBy) || [],
        [seatData, sortBy],
    )

    // This Effect checks for any search params and updates the state if any were found
    useEffect(() => {
        // Check for sort query param on load
        const sortParams = queryParams.get('sort')

        const sortMethod = sortMethods.find(
            (method) => method.paramName === sortParams,
        )
        setSortBy(sortMethod || sortMethods[0])
    }, [queryParams])

    // TODO: Check over, but this shouldn't be needed as the queryParams is updated!
    // /** Callback to handle filtering the seat data based on users query */
    // const handleSearch = useCallback(
    //     (searchTerm: string) => {
    //         if (!searchTerm) {
    //             setSeatData(get)
    //         } else {
    //             const filteredData = filterResults(searchTerm, initialData)
    //             setSeatData(filteredData)
    //         }
    //     },
    //     [filterResults, getInitialData, initialData],
    // )

    // Loading State!
    if (!seatData) {
        return (
            <FullElectorateWidgetWrapper headingComponents={[]}>
                {Array(5) // Render 5 skeletons while loading
                    .fill(0)
                    .map((_, index) => (
                        <FullElectorateCard
                            key={index}
                            isLoading
                            onEvent={onEvent}
                        />
                    ))}
            </FullElectorateWidgetWrapper>
        )
    }

    // // Error State! (data has loaded, but there is no data)
    // if (!data.result) {
    //     return (
    //         <FullElectorateWidgetWrapper headingComponents={[]}>
    //             <ElectionDataFetchError />
    //         </FullElectorateWidgetWrapper>
    //     )
    // }

    const headingComponents = [
        <CustomPageSharing
            text="WA election 2025 electorate results | The West Australian"
            shareOptions={[
                'facebook',
                'linkedin',
                'twitter',
                'email',
                'clipboard',
            ]}
            loading={false}
            onEvent={onEvent}
        />,
        <ElectionSearchInput
            onEvent={onEvent}
            queryParams={queryParams}
            pathname={pathname}
            // onSearch={handleSearch}
        />,
        <ElectionFilter
            headingText="Sort by"
            queryFilterParam="sort"
            dataLayerEventOriginator="ElectionSort"
            filterItems={sortMethods}
            onEvent={onEvent}
            dataLayerEventName={DataLayerEventName.searchFilter}
            defaultParamName={sortMethods[0]} // The default will always be the first sort option
            queryParams={queryParams}
            pathname={pathname}
            selectedItem={sortBy}
            setSelectedItem={setSortBy}
        />,
        <TheSeatsDivider hideOnDesktop hasMargin />,
    ]

    // No Search Results State!
    if (searchTerm && sortedData && sortedData.length <= 0) {
        return (
            <FullElectorateWidgetWrapper headingComponents={headingComponents}>
                <TheSeatsNoResultsText>
                    No results for ‘{searchTerm}’
                </TheSeatsNoResultsText>
                <TheSeatsDivider />
            </FullElectorateWidgetWrapper>
        )
    }

    return (
        <FullElectorateWidgetWrapper headingComponents={headingComponents}>
            {sortedData.map((seat) => (
                <FullElectorateCard
                    key={seat.seatId}
                    seat={seat}
                    onEvent={onEvent}
                />
            ))}
        </FullElectorateWidgetWrapper>
    )
}

const FullElectorateWidgetWrapper = ({
    headingComponents,
    children,
}: PropsWithChildren<{ headingComponents: JSX.Element[] }>) => {
    return (
        <StyledElectorateWidgetContainer>
            <StyledTitleContainer>
                <StyledElectorateTitle>
                    Electorate Results
                </StyledElectorateTitle>
                <ElectionText>
                    Live tracking of the electorate results as they are called.
                </ElectionText>
                {headingComponents}
            </StyledTitleContainer>
            {children}
        </StyledElectorateWidgetContainer>
    )
}
